<template>
  <div class="ckeditor-wrapper">
    <div ref="editor" class="ckeditor-editor"></div>
  </div>
</template>

<script>
export default {
  name: "Ckeditor",
  props: {
    value: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      editor: null
    }
  },
  mounted() {
    const {
      ClassicEditor,
      Autosave,
      Essentials,
      Paragraph,
      ImageUtils,
      ImageEditing,
      Fullscreen,
      Autoformat,
      TextTransformation,
      Bold,
      Italic,
      Underline,
      Strikethrough,
      Code,
      Subscript,
      Superscript,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      Highlight,
      ImageInline,
      ImageToolbar,
      ImageBlock,
      ImageUpload,
      CloudServices,
      Link,
      ImageInsertViaUrl,
      AutoImage,
      ImageStyle,
      LinkImage,
      ImageCaption,
      ImageTextAlternative,
      PlainTableOutput,
      TableCaption,
      Table,
      TableToolbar,
      ShowBlocks,
      SourceEditing,
      GeneralHtmlSupport,
      HtmlComment,
      AutoLink,
      Style,
      Heading,
      BlockQuote,
      HorizontalLine,
      Indent,
      IndentBlock,
      Alignment,
      BlockToolbar
    } = window.CKEDITOR;

    const LICENSE_KEY =
        'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3OTA5ODU1OTksImp0aSI6ImE3MWRmZTJjLTJkNWEtNGU5Yi05YTZlLWRjNjM2MGJhMGNkNCIsInVzYWdlRW5kcG9pbnQiOiJodHRwczovL3Byb3h5LWV2ZW50LmNrZWRpdG9yLmNvbSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsiY2xvdWQiLCJkcnVwYWwiXSwiZmVhdHVyZXMiOlsiRFJVUCIsIkUyUCIsIkUyVyJdLCJyZW1vdmVGZWF0dXJlcyI6WyJQQiIsIlJGIiwiU0NIIiwiVENQIiwiVEwiLCJUQ1IiLCJJUiIsIlNVQSIsIkI2NEEiLCJMUCIsIkhFIiwiUkVEIiwiUEZPIiwiV0MiLCJGQVIiLCJCS00iLCJGUEgiLCJNUkUiXSwidmMiOiIyNTI4OTFhMCJ9.rz7d9V4Ia522LrLKvzZ_SOHv9Hz6HVNrBQNH7FZ_hTLsbGFGv5Beuug4fxesvTs-RksKjVztUFc1FE-43TU6RA'
    const editorConfig = {
      toolbar: {
        items: [
          'undo', 'redo',
          '|',
          'bold', 'italic', 'underline',
          '|',
          'fontColor',
          'link',
          'blockQuote',
          '|',
          'bulletedList', 'numberedList',
          '|',
          'alignment'
        ]
      },
      plugins: [
        Alignment,
        Autoformat,
        AutoImage,
        AutoLink,
        Autosave,
        BlockQuote,
        BlockToolbar,
        Bold,
        CloudServices,
        Code,
        Essentials,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        Fullscreen,
        GeneralHtmlSupport,
        Heading,
        Highlight,
        HorizontalLine,
        HtmlComment,
        ImageBlock,
        ImageCaption,
        ImageEditing,
        ImageInline,
        ImageInsertViaUrl,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        ImageUtils,
        Indent,
        IndentBlock,
        Italic,
        Link,
        LinkImage,
        Paragraph,
        PlainTableOutput,
        ShowBlocks,
        SourceEditing,
        Strikethrough,
        Style,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableToolbar,
        TextTransformation,
        Underline
      ],
      blockToolbar: [
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'insertTable',
        '|',
        'outdent',
        'indent'
      ],
      fontFamily: {
        supportAllValues: true
      },
      fontSize: {
        options: [10, 12, 14, 'default', 18, 20, 22],
        supportAllValues: true
      },
      fullscreen: false,
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph'
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1'
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2'
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3'
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4'
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5'
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6'
          }
        ]
      },
      htmlSupport: {
        allow: [
          {
            name: /^.*$/,
            styles: true,
            attributes: true,
            classes: true
          }
        ]
      },
      image: {
        toolbar: ['toggleImageCaption', 'imageTextAlternative', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText']
      },
      initialData: '',
      language: 'fr',
      licenseKey: LICENSE_KEY,
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file'
            }
          }
        }
      },
      menuBar: {
        isVisible: false
      },
      placeholder: 'Commence à écrire ton contenu ici...',
      typing: {
        transformations: {
          remove: ['quotes']
        }
      },
      style: {
        definitions: [
          {
            name: 'Article category',
            element: 'h3',
            classes: ['category']
          },
          {
            name: 'Title',
            element: 'h2',
            classes: ['document-title']
          },
          {
            name: 'Subtitle',
            element: 'h3',
            classes: ['document-subtitle']
          },
          {
            name: 'Info box',
            element: 'p',
            classes: ['info-box']
          },
          {
            name: 'CTA Link Primary',
            element: 'a',
            classes: ['button', 'button--green']
          },
          {
            name: 'CTA Link Secondary',
            element: 'a',
            classes: ['button', 'button--black']
          },
          {
            name: 'Marker',
            element: 'span',
            classes: ['marker']
          },
          {
            name: 'Spoiler',
            element: 'span',
            classes: ['spoiler']
          }
        ]
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
      }
    };

    ClassicEditor.create(this.$refs.editor, editorConfig)
        .then(editor => {
          this.editor = editor
          editor.setData(this.value)

          // Quand on tape dans l’éditeur, on émet la valeur à Vue
          editor.model.document.on('change:data', () => {
            this.$emit('input', editor.getData())
          })
        })
        .catch(error => {
          console.error('Erreur CKEditor:', error)
        })
  },
  watch: {
    value(newVal) {
      if (this.editor && newVal !== this.editor.getData()) {
        this.editor.setData(newVal)
      }
    }
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
    }
  }
}
</script>
<style scoped>
.ckeditor-wrapper {
  max-height: calc(100vh - 350px);
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
}

/* La zone editable scrollable */
.ckeditor-wrapper .ck-editor__editable {
  min-height: 400px;
  max-height: 60vh;
}

/* Empêche l’éditeur de pousser les colonnes */
.ckeditor-editor {
  height: 100%;
}
.ck-editor__top {
  border: none !important;
}

.ck-toolbar {
  border: none !important;
  background: #f8f9fa;
}

.ck-editor__editable {
  border: none !important;
  box-shadow: none !important;
  padding: 16px !important;
  font-size: 0.95rem;
}

.ck.ck-editor__main {
  height: 100%;
}

.ck-editor__editable_inline {
  min-height: 400px;
}

</style>
